import { useTranslations } from "next-intl";
import { type Resolver, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCountry } from "@/config/country-context";
import type { AddedDebtFormItem } from "../types";
import { DEBT_TYPES_BY_COUNTRY, type DebtType } from "../utils/debt-types";

export interface ConfirmDebtForm {
	addedDebts: AddedDebtFormItem[];
	confirm: boolean;
}

export interface AddedDebtValidationMessages {
	typeRequired: string;
	amountRequired: string;
	amountAboveZero: string;
}

export const buildAddedDebtSchema = (
	messages: AddedDebtValidationMessages,
	allowedTypes: readonly DebtType[]
) =>
	z.object({
		type: z
			.string({ error: messages.typeRequired })
			.refine(
				(value) => (allowedTypes as readonly string[]).includes(value),
				messages.typeRequired
			),
		// The number field reports an emptied input as `null`, which coerces to 0.
		amount: z.preprocess(
			(value) => (value === null || value === "" ? undefined : value),
			z.coerce
				.number({ error: messages.amountRequired })
				.positive(messages.amountAboveZero)
		),
		saved: z.boolean().optional(),
	});

const unusedMessages: AddedDebtValidationMessages = {
	typeRequired: "",
	amountRequired: "",
	amountAboveZero: "",
};

/** Whether the debt's own fields pass, regardless of whether it is saved. */
export const isAddedDebtComplete = (
	debt: AddedDebtFormItem,
	allowedTypes: readonly DebtType[]
): boolean =>
	buildAddedDebtSchema(unusedMessages, allowedTypes).safeParse(debt).success;

export const isAddedDebtSaved = (
	debt: AddedDebtFormItem,
	allowedTypes: readonly DebtType[]
): boolean => debt.saved === true && isAddedDebtComplete(debt, allowedTypes);

export const useConfirmDebtForm = () => {
	const t = useTranslations("pages.ConfirmDebtPage");
	const country = useCountry();
	const allowedTypes = DEBT_TYPES_BY_COUNTRY[country];

	const debtSchema = buildAddedDebtSchema(
		{
			typeRequired: t("errors.typeRequired"),
			amountRequired: t("errors.amountRequired"),
			amountAboveZero: t("errors.amountAboveZero"),
		},
		allowedTypes
	).refine((debt) => debt.saved === true, {
		message: t("errors.unsavedDebt"),
		path: ["saved"],
	});

	const schema = z.object({
		addedDebts: z.array(debtSchema),
		confirm: z.literal(true, { error: t("errors.confirmRequired") }),
	});

	return {
		useForm: useForm<ConfirmDebtForm, unknown, ConfirmDebtForm>({
			resolver: zodResolver(schema) as unknown as Resolver<
				ConfirmDebtForm,
				unknown,
				ConfirmDebtForm
			>,
			mode: "onTouched",
			defaultValues: {
				addedDebts: [],
				confirm: false,
			},
		}),
		schema,
		allowedTypes,
	};
};
